import React, { useState, useEffect, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import {
  ArrowLeftIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  CheckCircleIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  LightBulbIcon,
  ExclamationCircleIcon,
  ChevronUpDownIcon
} from '@heroicons/react/24/outline';
import { Combobox, Transition } from '@headlessui/react';
import CaseProgressFlow from '../../components/CaseProgressFlow';
import ActivitiesTab from '../../components/ActivitiesTab';
import { useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import RichTextEditor from '../../components/RichTextEditor';
import SearchableDropdown from '../../components/SearchableDropdown';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { fetchAssignmentGroupMembers } from '../../utils/groupMembers';
import { sanitizeNoteInput } from '../../utils/sanitize';
import { trackCaseOperation } from '../../utils/appInsights';
import { useAuth } from '../../contexts/AuthContext';
import { useIsServiceDeskUser } from '../../hooks/useIsServiceDeskUser';

interface FormData {
  shortDescription: string;
  description: string;
  channel: string;
  category: string;
  impact: string;
  urgency: string;
  assignmentGroupId: string;
  assignedTo: string;
  state: string;
  caseType: string;
  resolutionNotes?: string;
  resolutionCode?: string;
  resourceId?: string | null;
  servicePlanId?: string | null;
}

const priorityLabels = {
  'critical_1': 'Critical',
  'high_2': 'High',
  'moderate_3': 'Moderate',
  'low_4': 'Low',
  'planning_5': 'Planning'
};

const CaseEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { formatDateTime } = useDateFormatter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, loading: permissionsLoading } = useDynamicPermissions();
  const hasPermission = can('cases', 'update');
  const { user } = useAuth();
  const isServiceDeskUser = useIsServiceDeskUser();

  // Priority calculation function (matches backend logic)
  const calculatePriorityFromImpactUrgency = (impact: string, urgency: string): string => {
    if (!impact || !urgency) return 'planning_5';

    const impactNum = parseInt(impact.split('_')[1]);
    const urgencyNum = parseInt(urgency.split('_')[1]);

    if (impactNum === 1 && urgencyNum === 1) return 'critical_1';
    if ((impactNum === 1 && urgencyNum === 2) || (impactNum === 2 && urgencyNum === 1)) return 'high_2';
    if ((impactNum === 1 && urgencyNum === 3) || (impactNum === 2 && urgencyNum === 2) || (impactNum === 3 && urgencyNum === 1)) return 'moderate_3';
    if ((impactNum === 2 && urgencyNum === 3) || (impactNum === 3 && urgencyNum === 2)) return 'low_4';
    if (impactNum === 3 && urgencyNum === 3) return 'planning_5';

    return 'planning_5';
  };

  // Fetch case data
  const { data: caseData, isLoading } = useQuery({
    queryKey: ['case', id],
    queryFn: async () => {
      const response = await api.get(`/cases/${id}`);
      return response.data;
    },
    enabled: !!id
  });

  // Fetch activities data for progress flow
  const { data: activities } = useQuery({
    queryKey: ['case', id, 'activities'],
    queryFn: async () => {
      const response = await api.get(`/cases/${id}/activities`);
      return response.data;
    },
    enabled: !!id
  });

  const [formData, setFormData] = useState<FormData>({
    shortDescription: '',
    description: '',
    channel: 'web',
    category: 'issue',
    impact: 'low_3',
    urgency: 'low_3',
    assignmentGroupId: '',
    assignedTo: '',
    state: 'new',
    caseType: 'incident',
    resolutionNotes: '',
    resolutionCode: '',
    resourceId: null
  });

  const [activeTab, setActiveTab] = useState<'edit' | 'communication' | 'timeline' | 'activities'>('edit');
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState<'customer_note' | 'work_note'>('customer_note');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when case is loaded
  useEffect(() => {
    if (caseData) {
      setFormData({
        shortDescription: caseData.shortDescription || '',
        description: caseData.description || '',
        channel: caseData.channel || 'web',
        category: caseData.category || 'issue',
        impact: caseData.impact || 'low_3',
        urgency: caseData.urgency || 'low_3',
        assignmentGroupId: caseData.assignmentGroupId || caseData.AssignmentGroup?.id || '',
        assignedTo: caseData.assignedTo || caseData.assignedToId || '',
        state: caseData.state || 'new',
        caseType: caseData.caseType || 'incident',
        resolutionNotes: caseData.resolutionNotes || '',
        resolutionCode: caseData.resolutionCode || '',
        resourceId: caseData.resourceId || caseData.Resource?.id || null,
        servicePlanId: caseData.servicePlanId || caseData.ServicePlan?.id || null
      });
    }
  }, [caseData]);

  // Fetch dropdowns data
  const { data: assignmentGroupsData } = useQuery({
    queryKey: ['assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups/assignment-groups');
      return response.data;
    }
  });

  const { data: groupMembersData } = useQuery({
    queryKey: ['group-members', formData.assignmentGroupId, caseData?.customerId || caseData?.Customer?.id],
    queryFn: async () => {
      if (!formData.assignmentGroupId) return [];
      return fetchAssignmentGroupMembers(
        formData.assignmentGroupId,
        caseData?.customerId || caseData?.Customer?.id || null
      );
    },
    enabled: !!formData.assignmentGroupId
  });

  // Fetch resources filtered by engagement for the Resource dropdown
  const { data: resourcesData } = useQuery({
    queryKey: ['resources', caseData?.engagementId || caseData?.Engagement?.id],
    queryFn: async () => {
      const engagementId = caseData?.engagementId || caseData?.Engagement?.id;
      if (!engagementId) return { resources: [] };
      const response = await api.get(`/resources?engagementId=${engagementId}`);
      return response.data;
    },
    enabled: !!caseData && !!(caseData?.engagementId || caseData?.Engagement?.id)
  });

  const [resourceQuery, setResourceQuery] = useState('');
  const [servicePlanQuery, setServicePlanQuery] = useState('');

  // Fetch service plans based on engagement
  const { data: servicePlansData } = useQuery({
    queryKey: ['service-plans', caseData?.engagementId || caseData?.Engagement?.id],
    queryFn: async () => {
      const engId = caseData?.engagementId || caseData?.Engagement?.id;
      if (!engId) return { plans: [] };
      const response = await api.get(`/engagements/${engId}/service-plans`);
      return response.data;
    },
    enabled: !!caseData && !!(caseData?.engagementId || caseData?.Engagement?.id)
  });

  const updateCaseMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await api.put(`/cases/${id}`, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['case', id] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      
      // Log Case Details Modified
      if (user) {
        const caseNumber = data?.case?.number || data?.number || caseData?.number || caseData?.id || id || 'Unknown';
        trackCaseOperation(
          'Case Details - Edit',
          user.email || user.firstName || 'Unknown',
          {
            action: 'Case Details Modified',
            caseNumber: caseNumber,
          },
          {
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role,
            roleType: user.roleType,
            roleLevel: user.roleLevel,
            userClass: user.class,
            customerId: user.customerId,
            customerName: user.customerName,
            engagementId: user.engagementId,
            engagementName: user.engagementName,
            isServiceDeskUser: isServiceDeskUser,
          }
        );
      }
      
      navigate(`/cases/${id}`);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update case';
      setErrors({ submit: message });
    }
  });

  const addNoteMutation = useMutation({
    mutationFn: async (data: { note: string; noteType: string }) => {
      const sanitizedNote = sanitizeNoteInput(data.note);
      const endpoint = data.noteType === 'work_note' ? 'work-notes' : 'work-notes';
      const response = await api.post(`/cases/${id}/${endpoint}`, {
        note: sanitizedNote,
        isPublic: data.noteType === 'customer_note',
      });
      return response.data;
    },
    onSuccess: () => {
      setNewNote('');
      queryClient.invalidateQueries({ queryKey: ['case', id] });
      queryClient.invalidateQueries({ queryKey: ['case', id, 'activities'] });
      toast.success('Communication added successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to add communication';
      toast.error(message);
    }
  });

  const acknowledgeCaseMutation = useMutation({
    mutationFn: async () => {
      const response = await api.put(`/cases/${id}/acknowledge`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case', id] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case', id, 'activities'] });
      toast.success('Case acknowledged successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to acknowledge case';
      toast.error(message);
    }
  });

  const changeStateMutation = useMutation({
    mutationFn: async (newState: string) => {
      const response = await api.put(`/cases/${id}`, { state: newState });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case', id] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case', id, 'activities'] });
      toast.success('Case state updated successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update case state';
      toast.error(message);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const validationErrors: Record<string, string> = {};
    if (!formData.shortDescription) {
      validationErrors.shortDescription = 'Short description is required';
    }
    if (!formData.channel) {
      validationErrors.channel = 'Channel is required';
    }
    if (!formData.category) {
      validationErrors.category = 'Category is required';
    }
    if (!formData.assignmentGroupId) {
      validationErrors.assignmentGroupId = 'Assignment Group is required';
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const priority = calculatePriorityFromImpactUrgency(formData.impact, formData.urgency);

    updateCaseMutation.mutate({
      ...formData,
      priority
    } as any);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleRichTextChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleAddNote = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    addNoteMutation.mutate({ note: newNote, noteType });
  };

  const handleAcknowledge = () => {
    acknowledgeCaseMutation.mutate();
  };

  const handleStateChange = (newState: string) => {
    changeStateMutation.mutate(newState);
  };

  // Get available state transitions based on current state
  const getAvailableTransitions = (currentState: string) => {
    const transitions = [];

    switch (currentState) {
      case 'new':
        transitions.push({
          state: 'open',
          label: 'Acknowledge',
          icon: CheckCircleIcon,
          action: handleAcknowledge,
          className: 'bg-green-600 hover:bg-green-700'
        });
        break;

      case 'open':
        transitions.push(
          {
            state: 'in_progress',
            label: 'Start Work',
            icon: PlayIcon,
            action: () => handleStateChange('in_progress'),
            className: 'bg-blue-600 hover:bg-blue-700'
          },
          {
            state: 'awaiting_info',
            label: 'Awaiting Info',
            icon: ExclamationCircleIcon,
            action: () => handleStateChange('awaiting_info'),
            className: 'bg-yellow-600 hover:bg-yellow-700'
          },
          {
            state: 'on_hold',
            label: 'Put On Hold',
            icon: PauseIcon,
            action: () => handleStateChange('on_hold'),
            className: 'bg-orange-600 hover:bg-orange-700'
          },
          {
            state: 'solution_proposed',
            label: 'Propose Solution',
            icon: LightBulbIcon,
            action: () => handleStateChange('solution_proposed'),
            className: 'bg-purple-600 hover:bg-purple-700'
          },
          {
            state: 'closed',
            label: 'Close Case',
            icon: CheckCircleIcon,
            action: () => handleStateChange('closed'),
            className: 'bg-green-600 hover:bg-green-700'
          },
          {
            state: 'cancelled',
            label: 'Cancel',
            icon: StopIcon,
            action: () => handleStateChange('cancelled'),
            className: 'bg-red-600 hover:bg-red-700'
          }
        );
        break;

      case 'in_progress':
        transitions.push(
          {
            state: 'awaiting_info',
            label: 'Awaiting Info',
            icon: ExclamationCircleIcon,
            action: () => handleStateChange('awaiting_info'),
            className: 'bg-yellow-600 hover:bg-yellow-700'
          },
          {
            state: 'on_hold',
            label: 'Put On Hold',
            icon: PauseIcon,
            action: () => handleStateChange('on_hold'),
            className: 'bg-orange-600 hover:bg-orange-700'
          },
          {
            state: 'solution_proposed',
            label: 'Propose Solution',
            icon: LightBulbIcon,
            action: () => handleStateChange('solution_proposed'),
            className: 'bg-purple-600 hover:bg-purple-700'
          },
          {
            state: 'closed',
            label: 'Close Case',
            icon: CheckCircleIcon,
            action: () => handleStateChange('closed'),
            className: 'bg-green-600 hover:bg-green-700'
          },
          {
            state: 'cancelled',
            label: 'Cancel',
            icon: StopIcon,
            action: () => handleStateChange('cancelled'),
            className: 'bg-red-600 hover:bg-red-700'
          }
        );
        break;

      case 'awaiting_info':
      case 'on_hold':
      case 'solution_proposed':
        transitions.push(
          {
            state: 'in_progress',
            label: 'Resume Work',
            icon: PlayIcon,
            action: () => handleStateChange('in_progress'),
            className: 'bg-blue-600 hover:bg-blue-700'
          },
          {
            state: 'closed',
            label: 'Close Case',
            icon: CheckCircleIcon,
            action: () => handleStateChange('closed'),
            className: 'bg-green-600 hover:bg-green-700'
          },
          {
            state: 'cancelled',
            label: 'Cancel',
            icon: StopIcon,
            action: () => handleStateChange('cancelled'),
            className: 'bg-red-600 hover:bg-red-700'
          }
        );
        break;

      default:
        break;
    }

    return transitions;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Calculate current priority for display
  const currentPriority = calculatePriorityFromImpactUrgency(formData.impact, formData.urgency);
  const priorityMatch = currentPriority?.match(/_(\d)/);
  const priorityNumber = priorityMatch ? priorityMatch[1] : '';
  const priorityLabel = priorityLabels[currentPriority as keyof typeof priorityLabels] || 'Planning';
  const priorityDisplay = priorityNumber ? `${priorityNumber} - ${priorityLabel}` : priorityLabel;

  // Show loading while checking permissions
  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check permission
  if (!hasPermission) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          You don't have permission to update cases.
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-4">
        <button
          onClick={() => navigate('/cases')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          Back to Cases
        </button>
      </div>

      {/* Dynamic Workflow Progress */}
      {activities && (
        <CaseProgressFlow
          activities={activities}
          currentState={caseData.state}
        />
      )}

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Edit Case {caseData?.number}
            </h2>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border border-gray-300 bg-gray-50 text-gray-700">
              Priority {priorityDisplay}
            </span>
          </div>
        </div>

        {/* Available Actions */}
        {caseData?.state && caseData.state !== 'closed' && caseData.state !== 'cancelled' && caseData.state !== 'resolved' && (
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-700 mr-2">Available Actions:</span>
              {getAvailableTransitions(caseData.state).map((transition) => {
                const Icon = transition.icon;
                return (
                  <button
                    key={transition.state}
                    onClick={transition.action}
                    disabled={acknowledgeCaseMutation.isPending || changeStateMutation.isPending}
                    className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white ${transition.className} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-opacity-50 disabled:opacity-50`}
                  >
                    <Icon className="h-4 w-4 mr-1" />
                    {(acknowledgeCaseMutation.isPending || changeStateMutation.isPending) ? 'Processing...' : transition.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex px-6">
            <button
              onClick={() => setActiveTab('edit')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'edit'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('communication')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'communication'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Notes
            </button>
            <button
              onClick={() => setActiveTab('timeline')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'timeline'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Timeline
            </button>
            <button
              onClick={() => setActiveTab('activities')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'activities'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Activity
            </button>
          </nav>
        </div>

        {activeTab === 'edit' && (
          <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Row 1: Number (read-only) and State */}
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Number</label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={caseData?.number || ''}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={(() => {
                        const stateLabels: Record<string, string> = {
                          'new': 'New',
                          'open': 'Open',
                          'in_progress': 'In Progress',
                          'work_in_progress': 'Work in Progress',
                          'awaiting_info': 'Awaiting Info',
                          'on_hold': 'On Hold',
                          'solution_proposed': 'Solution Proposed',
                          'pending_customer': 'Pending Customer',
                          'pending_vendor': 'Pending Vendor',
                          'resolved': 'Resolved',
                          'closed': 'Closed',
                          'cancelled': 'Cancelled'
                        };
                        return stateLabels[caseData?.state] || caseData?.state || 'New';
                      })()}
                      readOnly
                      disabled
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8 cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Impact and Urgency */}
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Impact</label>
                  </div>
                  <div className="col-span-9">
                    <select
                      name="impact"
                      value={formData.impact}
                      onChange={handleChange}
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="high_1">1 - High</option>
                      <option value="medium_2">2 - Medium</option>
                      <option value="low_3">3 - Low</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Urgency</label>
                  </div>
                  <div className="col-span-9">
                    <select
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleChange}
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="high_1">1 - High</option>
                      <option value="medium_2">2 - Medium</option>
                      <option value="low_3">3 - Low</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3: Priority (computed) */}
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={priorityDisplay}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 4: Channel and Category */}
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Channel</label>
                  </div>
                  <div className="col-span-9">
                    <select
                      name="channel"
                      value={formData.channel}
                      onChange={handleChange}
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="web">Web</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="chat">Chat</option>
                      <option value="walk_in">Walk In</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  </div>
                  <div className="col-span-9">
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="question">Question</option>
                      <option value="issue">Issue</option>
                      <option value="request">Request</option>
                      <option value="change">Change</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 6: Customer and Engagement (read-only) */}
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={caseData?.Customer?.accountName || '-'}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Engagement</label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={caseData?.Engagement ? `${caseData.Engagement.name}${caseData.Engagement.status ? ` - ${caseData.Engagement.status}` : ''}` : '-'}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 7: Contact and Service Plan (read-only) */}
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={caseData?.contact ? `${caseData.contact.firstName || ''} ${caseData.contact.lastName || ''}`.trim() || caseData.contact.email || '-' : '-'}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Service Plan</label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.servicePlanId || ''}
                      onChange={(value: string) => setFormData({ ...formData, servicePlanId: value || null })}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                          displayValue={(servicePlanId: string) => {
                            const plan = (servicePlansData?.plans || servicePlansData?.servicePlans || []).find((p: any) => p.id === servicePlanId);
                            return plan ? plan.name : '';
                          }}
                          onChange={(e) => setServicePlanQuery(e.target.value)}
                          placeholder="Select Service Plan"
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setServicePlanQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(servicePlansData?.plans || servicePlansData?.servicePlans || [])
                              .filter((plan: any) => plan && plan.id && plan.name && plan.name.toLowerCase().includes(servicePlanQuery.toLowerCase()))
                              .map((plan: any) => (
                                <Combobox.Option
                                  key={plan.id}
                                  value={plan.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ selected, active }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                        {plan.name}
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                          active ? 'text-white' : 'text-indigo-600'
                                        }`}>
                                          <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 8: Resource (editable) and Resource ID (read-only) */}
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Resource</label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.resourceId || ''}
                      onChange={(value: string) => {
                        setFormData({ ...formData, resourceId: value || null });
                      }}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                          displayValue={(resourceId: string) => {
                            const resource = (resourcesData?.resources || []).find((r: any) => r.id === resourceId);
                            return resource ? resource.name : '';
                          }}
                          onChange={(event) => setResourceQuery(event.target.value)}
                          placeholder="Search resources..."
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                        </Combobox.Button>
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          <Combobox.Option
                            value=""
                            className={({ active }) =>
                              `relative cursor-default select-none py-2 pl-3 pr-9 ${
                                active ? 'bg-blue-600 text-white' : 'text-gray-900'
                              }`
                            }
                          >
                            -- None --
                          </Combobox.Option>
                          {(resourcesData?.resources || [])
                            .filter((resource: any) =>
                              resource.name.toLowerCase().includes(resourceQuery.toLowerCase())
                            )
                            .map((resource: any) => (
                              <Combobox.Option
                                key={resource.id}
                                value={resource.id}
                                className={({ active }) =>
                                  `relative cursor-default select-none py-2 pl-3 pr-9 ${
                                    active ? 'bg-blue-600 text-white' : 'text-gray-900'
                                  }`
                                }
                              >
                                {resource.name}
                              </Combobox.Option>
                            ))}
                        </Combobox.Options>
                      </div>
                    </Combobox>
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Resource ID</label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={(() => {
                        const selectedResource = (resourcesData?.resources || []).find((r: any) => r.id === formData.resourceId);
                        return selectedResource?.resourceId || caseData?.resourceIdValue || '-';
                      })()}
                      readOnly
                      disabled
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8 cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 9: Assignment Group and Assigned To (editable) */}
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assignment Group <span className="text-red-500">*</span></label>
                  </div>
                  <div className="col-span-9">
                    <SearchableDropdown
                      value={formData.assignmentGroupId || null}
                      onChange={(val) => {
                        setFormData(prev => ({
                          ...prev,
                          assignmentGroupId: val || '',
                          assignedTo: '' // Reset assigned to when group changes
                        }));
                        if (errors.assignmentGroupId) {
                          setErrors(prev => ({ ...prev, assignmentGroupId: '' }));
                        }
                      }}
                      options={(assignmentGroupsData || []).map((group: any) => ({
                        value: group.id,
                        label: group.name
                      }))}
                      placeholder="Select Assignment Group"
                    />
                    {errors.assignmentGroupId && (
                      <p className="mt-1 text-sm text-red-600">{errors.assignmentGroupId}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                  </div>
                  <div className="col-span-9">
                    <SearchableDropdown
                      value={formData.assignedTo || null}
                      onChange={(val) => {
                        setFormData(prev => ({
                          ...prev,
                          assignedTo: val || ''
                        }));
                        if (errors.assignedTo) {
                          setErrors(prev => ({ ...prev, assignedTo: '' }));
                        }
                      }}
                      options={(groupMembersData || []).map((member: any) => ({
                        value: member.id,
                        label: `${member.firstName} ${member.lastName}`
                      }))}
                      placeholder="Select User"
                      disabled={!formData.assignmentGroupId}
                    />
                    {errors.assignedTo && (
                      <p className="mt-1 text-sm text-red-600">{errors.assignedTo}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 10: Short Description */}
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
                  </div>
                  <div className="col-span-10">
                    <input
                      type="text"
                      name="shortDescription"
                      value={formData.shortDescription}
                      onChange={handleChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:ring-indigo-500 focus:border-indigo-500 ${
                        errors.shortDescription ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.shortDescription && (
                      <p className="mt-1 text-sm text-red-600">{errors.shortDescription}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 11: Description */}
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  </div>
                  <div className="col-span-10">
                    <RichTextEditor
                      value={formData.description || ''}
                      onChange={(value) => handleRichTextChange('description', value)}
                      placeholder="Enter case description"
                      minHeight="120px"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Resolution Information (if resolved or closed) */}
            {(formData.state === 'resolved' || formData.state === 'closed') && (
              <>
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Resolution Code</label>
                      </div>
                      <div className="col-span-9">
                        <select
                          name="resolutionCode"
                          value={formData.resolutionCode}
                          onChange={handleChange}
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="">Select Resolution Code</option>
                          <option value="solved">Solved</option>
                          <option value="workaround">Workaround Provided</option>
                          <option value="no_issue_found">No Issue Found</option>
                          <option value="duplicate">Duplicate</option>
                          <option value="wont_fix">Won't Fix</option>
                          <option value="customer_resolved">Customer Resolved</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Resolution Notes</label>
                      </div>
                      <div className="col-span-10">
                        <RichTextEditor
                          value={formData.resolutionNotes || ''}
                          onChange={(value) => handleRichTextChange('resolutionNotes', value)}
                          placeholder="Enter resolution notes"
                          minHeight="100px"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Error Display */}
            {errors.submit && (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-800">{errors.submit}</p>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate(`/cases/${id}`)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateCaseMutation.isPending}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {updateCaseMutation.isPending ? 'Updating...' : 'Update Case'}
              </button>
            </div>
          </div>
        </form>
        )}

        {/* Communication Tab */}
        {activeTab === 'communication' && (
          <div className="p-6">
            {/* Add Note Form */}
            <form onSubmit={handleAddNote} className="mb-6">
              <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700">Add Communication</label>
                <RichTextEditor
                  value={newNote}
                  onChange={setNewNote}
                  placeholder="Enter your message..."
                  minHeight="100px"
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="customer_note"
                      checked={noteType === 'customer_note'}
                      onChange={(e) => setNoteType(e.target.value as 'customer_note')}
                      className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Customer Visible</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="work_note"
                      checked={noteType === 'work_note'}
                      onChange={(e) => setNoteType(e.target.value as 'work_note')}
                      className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Internal Note</span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={addNoteMutation.isPending || !newNote.trim()}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {addNoteMutation.isPending ? 'Adding...' : 'Add Communication'}
                </button>
              </div>
            </form>

            {/* Communication History */}
            <div className="space-y-4">
              {caseData?.workNotes?.map((note: any) => {
                const isPublicNote = note.isPublic ?? (note.direction === 'outbound');
                const authorName = note.author && note.author.firstName && note.author.lastName
                  ? `${note.author.firstName} ${note.author.lastName}`
                  : 'Unknown User';
                const initials = note.author && note.author.firstName && note.author.lastName
                  ? `${note.author.firstName[0]}${note.author.lastName[0]}`
                  : 'U';
                const content = note.note || note.message || '';
                const ts = note.timestamp ? formatDateTime(note.timestamp) : '';
                return (
                  <div key={note.id || note._id} className={`rounded-lg p-4 ${
                    isPublicNote ? 'bg-blue-50' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-gray-500">
                            <span className="text-xs font-medium leading-none text-white">{initials}</span>
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{authorName}</p>
                          <p className="mt-1 text-sm text-gray-600">{content}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 text-xs text-gray-500">
                        {isPublicNote ? (
                          <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Customer Visible</span>
                        ) : (
                          <span className="bg-gray-100 text-gray-800 px-2 py-0.5 rounded">Internal</span>
                        )}
                        {ts && <span>{ts}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Timeline Tab */}
        {activeTab === 'timeline' && (
          <div className="p-6">
            <div className="flow-root">
              <ul className="-mb-8">
                {/* Created Event */}
                <li>
                  <div className="relative pb-8">
                    <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
                    <div className="relative flex space-x-3">
                      <div>
                        <span className="h-8 w-8 rounded-full bg-gray-400 flex items-center justify-center ring-8 ring-white">
                          <ClockIcon className="h-5 w-5 text-white" />
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p className="text-sm text-gray-500">Case created</p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-gray-500">
                          {formatDateTime(caseData?.createdAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>

                {/* Work Notes as Timeline Events */}
                {caseData?.workNotes?.map((note: any, noteIdx: number) => (
                  <li key={note.id || note._id}>
                    <div className="relative pb-8">
                      {noteIdx !== (caseData.workNotes?.length || 0) - 1 && (
                        <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
                      )}
                      <div className="relative flex space-x-3">
                        <div>
                          <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                            <ChatBubbleLeftRightIcon className="h-5 w-5 text-white" />
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 pt-1.5">
                          <div>
                            <p className="text-sm text-gray-500">
                              {(note.author && note.author.firstName && note.author.lastName ?
                                `${note.author.firstName} ${note.author.lastName}` :
                                'Unknown User')} added a {(note.isPublic ?? (note.direction === 'outbound')) ? 'customer visible' : 'internal'} note
                            </p>
                            <p className="mt-1 text-sm text-gray-700">{note.note || note.message}</p>
                          </div>
                          <div className="text-sm whitespace-nowrap text-gray-500 mt-1">
                            {note.timestamp ? formatDateTime(note.timestamp) : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}

                {/* Resolved Event */}
                {caseData?.resolvedAt && (
                  <li>
                    <div className="relative pb-8">
                      <div className="relative flex space-x-3">
                        <div>
                          <span className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center ring-8 ring-white">
                            <CheckIcon className="h-5 w-5 text-white" />
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                          <div>
                            <p className="text-sm text-gray-500">Case resolved</p>
                          </div>
                          <div className="text-right text-sm whitespace-nowrap text-gray-500">
                            {formatDateTime(caseData.resolvedAt)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}

        {/* Activities Tab */}
        {activeTab === 'activities' && id && (
          <div className="p-6">
            <ActivitiesTab caseId={id} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseEdit;